import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URLS } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  BookOpen, 
  Clock, 
  User, 
  Eye,
  Heart,
  Share2,
  Search,
  Filter,
  TrendingUp,
  Calendar,
  ArrowRight,
  Play,
  Video,
  GraduationCap,
  Award,
  Target,
  Lightbulb,
  Rocket,
  Star,
  Globe,
  Server,
  ShoppingCart,
  Palette
} from 'lucide-react';

const EducationPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [visibleCount, setVisibleCount] = useState(6);
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const [categories, setCategories] = useState([
    { id: 'all', name: 'Tüm Eğitimler', icon: BookOpen, count: 0 },
    { id: 'web-development', name: 'Web Geliştirme', icon: Globe, count: 0 },
    { id: 'ecommerce', name: 'E-ticaret', icon: ShoppingCart, count: 0 },
    { id: 'hosting', name: 'Hosting & Domain', icon: Server, count: 0 },
    { id: 'server', name: 'Sunucu Yönetimi', icon: Server, count: 0 },
    { id: 'seo', name: 'SEO & Pazarlama', icon: TrendingUp, count: 0 },
    { id: 'design', name: 'Tasarım', icon: Palette, count: 0 },
    { id: 'business', name: 'İş Geliştirme', icon: Target, count: 0 }
  ]);

  const [educationPosts, setEducationPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchEducations = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(API_URLS.EDUCATIONS);
        if (!res.ok) throw new Error('Education fetch failed');
        const data = await res.json();
        const posts = Array.isArray(data) ? data : (data.data || []);
        setEducationPosts(posts);
        setCategories(prev => prev.map(cat => ({
          ...cat,
          count: cat.id === 'all' ? posts.length : posts.filter(p => p.category === cat.id).length
        })));
      } catch (e) {
        console.error('Failed to fetch educations:', e);
        setError('Eğitimler yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };
    fetchEducations();
  }, []);

  const filteredPosts = educationPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.published_at || b.created_at) - new Date(a.published_at || a.created_at);
      case 'oldest':
        return new Date(a.published_at || a.created_at) - new Date(b.published_at || b.created_at);
      case 'popular':
        return (b.views || 0) - (a.views || 0);
      case 'likes':
        return (b.likes || 0) - (a.likes || 0);
      default:
        return 0;
    }
  });

  const visiblePosts = sortedPosts.slice(0, visibleCount);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.icon : BookOpen;
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Genel';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Eğitim Merkezi</h1>
            <p className="text-xl text-purple-100 mb-8">
              Teknoloji dünyasında uzmanlaşmak için ihtiyacınız olan tüm eğitimler burada
            </p>
            <div className="flex justify-center">
              <div className="relative w-full max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white w-5 h-5 z-10" />
                <Input
                  type="text"
                  placeholder="Eğitim ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="edu-input pl-10 pr-4 py-3 w-full rounded-lg border-0 bg-white/10 backdrop-blur-sm text-white placeholder-white focus:bg-white/20 focus:ring-2 focus:ring-white/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div 
        className="max-w-7xl mx-auto px-6 py-8"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Desktop Only */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Kategoriler
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {categories.map((category) => {
                      const Icon = category.icon;
                      return (
                        <button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                            selectedCategory === category.id
                              ? 'bg-purple-100 text-purple-700'
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="w-4 h-4" />
                            <span className="font-medium">{category.name}</span>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {category.count}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Sort Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Sıralama
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {[
                      { id: 'newest', name: 'En Yeni', icon: Calendar },
                      { id: 'oldest', name: 'En Eski', icon: Clock },
                      { id: 'popular', name: 'En Popüler', icon: Eye },
                      { id: 'likes', name: 'En Beğenilen', icon: Heart }
                    ].map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setSortBy(option.id)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                          sortBy === option.id
                            ? 'bg-purple-100 text-purple-700'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        <option.icon className="w-4 h-4" />
                        <span>{option.name}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Mobile Filters - Wrap Layout */}
          <div className="lg:hidden space-y-4 mb-6">
            {/* Mobile Categories */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Kategoriler
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedCategory === category.id
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{category.name}</span>
                      <Badge variant="secondary" className={selectedCategory === category.id ? 'bg-white text-purple-600' : 'bg-white'}>
                        {category.count}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-gray-600">Eğitimler yükleniyor...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <div className="text-red-500 mb-4">
                  <BookOpen className="w-16 h-16 mx-auto mb-4" />
                </div>
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Hata</h3>
                <p className="text-gray-500">{error}</p>
                <Button 
                  onClick={() => window.location.reload()} 
                  className="mt-4"
                  variant="outline"
                >
                  Tekrar Dene
                </Button>
              </div>
            )}

            {/* Content */}
            {!loading && !error && (
              <>
                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {selectedCategory === 'all' ? 'Tüm Eğitimler' : getCategoryName(selectedCategory)}
                    </h2>
                    <p className="text-gray-600">
                      {filteredPosts.length} eğitim bulundu
                    </p>
                  </div>
                </div>

                {/* Education Posts Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {visiblePosts.map((post) => {
                    const CategoryIcon = getCategoryIcon(post.category);
                    return (
                      <Card key={post.id} className="group hover:shadow-lg transition-all duration-300 flex flex-col">
                        <div className="relative">
                          {post.video_url && (
                            <div className="absolute top-4 right-4 z-10">
                              <Badge className="bg-red-500 text-white">
                                <Play className="w-3 h-3 mr-1" />
                                Video
                              </Badge>
                            </div>
                          )}
                          {post.image && (
                            <div className="aspect-video bg-gradient-to-br from-purple-100 to-indigo-100 rounded-t-lg overflow-hidden">
                              <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          )}
                        </div>
                        
                        <CardContent className="p-6 flex flex-col flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <CategoryIcon className="w-4 h-4 text-purple-600" />
                            <Badge variant="outline" className="text-xs">
                              {getCategoryName(post.category)}
                            </Badge>
                            {post.level && (
                              <Badge variant="secondary" className="text-xs">
                                {post.level}
                              </Badge>
                            )}
                          </div>
                          
                          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                            <Link to={`/egitim/${post.slug}`}>
                              {post.title}
                            </Link>
                          </h3>
                          
                          <p className="text-gray-600 mb-4 line-clamp-3">
                            {post.excerpt || post.content?.substring(0, 150) + '...'}
                          </p>
                          
                          <div className="mt-auto">
                            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  <span>{post.read_time || '5 dakika'}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Eye className="w-4 h-4" />
                                  <span>{post.views || 0}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Heart className="w-4 h-4" />
                                  <span>{post.likes || 0}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>{formatDate(post.published_at || post.created_at)}</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {post.author && (
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-600">{post.author}</span>
                                  </div>
                                )}
                              </div>
                              <Link to={`/egitim/${post.slug}`}>
                                <Button variant="outline" size="sm" className="group-hover:bg-purple-600 group-hover:text-white group-hover:border-purple-600">
                                  Devamını Oku
                                  <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Load More Button */}
                {visibleCount < filteredPosts.length && (
                  <div className="text-center">
                    <Button
                      onClick={() => setVisibleCount(prev => prev + 6)}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Daha Fazla Göster ({filteredPosts.length - visibleCount} kaldı)
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationPage;
